(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-tfk-knee-bundle]';

  function parseVariants(root) {
    try {
      return JSON.parse(root.getAttribute('data-variants') || '[]');
    } catch (error) {
      console.error('[ToeFix knee bundle] Could not parse product variants.', error);
      return [];
    }
  }

  function variantOptions(variant, optionCount) {
    return [variant.option1, variant.option2, variant.option3].slice(0, optionCount);
  }

  function initRoot(root) {
    if (!(root instanceof HTMLElement) || root.dataset.tfkInitialized === 'true') return;

    const productId = String(root.dataset.productId || '');
    const optionCount = Number(root.dataset.optionCount || 0);
    let optionNames = [];
    try { optionNames = JSON.parse(root.dataset.optionNames || '[]'); } catch (_error) { optionNames = []; }
    const variants = parseVariants(root);
    if (!productId || !variants.length) return;

    const section = root.closest('.shopify-section') || document;
    const productInfo = root.closest('.product-information') || section;
    const formComponent = Array.from(productInfo.querySelectorAll('product-form-component'))
      .find((element) => String(element.dataset.productId || '') === productId);
    const form = formComponent?.querySelector('form[action*="/cart/add"]');
    const variantInput = form?.querySelector('input[name="id"]');
    const secondItemInput = root.querySelector('[data-tfk-second-item]');

    if (!formComponent || !form || !variantInput || !secondItemInput) {
      const retries = Number(root.dataset.tfkRetries || 0);
      if (retries < 30) {
        root.dataset.tfkRetries = String(retries + 1);
        window.setTimeout(() => initRoot(root), 80);
      }
      return;
    }

    root.dataset.tfkInitialized = 'true';
    root.classList.add('is-ready');
    productInfo.classList.add('tfk-knee-bundle-ready');

    const currency = () =>
      window.Shopify?.currency?.active ||
      root.dataset.currency ||
      'USD';

    const formatMoney = (cents) => {
      const locale = document.documentElement.lang || 'en-US';
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency(),
          currencyDisplay: 'narrowSymbol',
        }).format((Number(cents) || 0) / 100);
      } catch (_error) {
        return `${currency()} ${((Number(cents) || 0) / 100).toFixed(2)}`;
      }
    };

    const rowValues = (row) =>
      Array.from(row?.querySelectorAll('[data-tfk-option-index]') || [])
        .sort((a, b) => Number(a.dataset.tfkOptionIndex) - Number(b.dataset.tfkOptionIndex))
        .map((select) => select.value);

    const findVariant = (row) => {
      if (!row) return null;
      const values = rowValues(row);
      return variants.find((variant) => {
        const options = variantOptions(variant, optionCount);
        return options.length === values.length &&
          options.every((value, index) => String(value ?? '') === String(values[index] ?? ''));
      }) || null;
    };

    const rowFor = (card, itemIndex) =>
      card?.querySelector(`[data-tfk-choice-row][data-item-index="${itemIndex}"]`) || null;

    const activePackCount = () =>
      Number(root.querySelector('input[name="tfk-knee-pack"]:checked')?.value ||
        root.querySelector('[data-tfk-pack-card].is-active')?.dataset.pack || 2);

    const compareValue = (variant) => {
      const price = Number(variant?.price) || 0;
      const compare = Number(variant?.compare_at_price) || 0;
      return compare > price ? compare : price;
    };

    function updateCardPrice(card) {
      const count = Number(card.dataset.pack);
      const first = findVariant(rowFor(card, 1));
      const second = count === 2 ? findVariant(rowFor(card, 2)) : null;
      const chosen = count === 1 ? [first] : [first, second];
      const valid = chosen.every((variant) => variant && variant.available !== false);
      const nowNode = card.querySelector('[data-tfk-pack-now]');
      const wasNode = card.querySelector('[data-tfk-pack-was]');
      const saveNode = card.querySelector('[data-tfk-pack-save]');

      if (!valid) {
        if (nowNode) nowNode.textContent = '—';
        if (wasNode) {
          wasNode.hidden = true;
          wasNode.textContent = '';
        }
        if (saveNode) {
          saveNode.hidden = true;
          saveNode.textContent = '';
        }
        return;
      }

      const currentTotal = count === 1
        ? Number(first.price)
        : Number(first.price) + Math.round(Number(second.price) * 0.5);
      const regularTotal = chosen.reduce((sum, variant) => sum + compareValue(variant), 0);
      const savings = Math.max(0, regularTotal - currentTotal);

      if (nowNode) nowNode.textContent = formatMoney(currentTotal);
      if (wasNode) {
        wasNode.hidden = regularTotal <= currentTotal;
        wasNode.textContent = regularTotal > currentTotal ? formatMoney(regularTotal) : '';
      }
      if (saveNode) {
        saveNode.hidden = savings <= 0;
        saveNode.textContent = savings > 0 ? `You save ${formatMoney(savings)}` : '';
      }
    }

    function updateMainPrice(variant) {
      if (!variant) return;
      const priceRoot = productInfo.querySelector('product-price') || productInfo.querySelector('.price');
      if (!priceRoot) return;

      const priceText = formatMoney(variant.price);
      const compareCents = Number(variant.compare_at_price) || 0;
      const hasCompare = compareCents > Number(variant.price);
      const saleNode = priceRoot.querySelector('.price-item--sale.price, .price__sale .price, .price__sale .money, [data-product-price]');
      const regularNode = priceRoot.querySelector('.price__regular .price, .price__regular .money, .price-item--regular.price');
      const targetNode = saleNode || regularNode;

      if (targetNode) {
        targetNode.textContent = priceText;
        targetNode.hidden = false;
      }
      if (regularNode && regularNode !== targetNode) {
        regularNode.textContent = priceText;
        regularNode.hidden = false;
      }

      const compareNode = priceRoot.querySelector('.compare-at-price, .price__compare .price, s.price, s');
      if (compareNode) {
        compareNode.hidden = !hasCompare;
        compareNode.style.display = hasCompare ? '' : 'none';
        if (hasCompare) compareNode.textContent = formatMoney(compareCents);
      }
      priceRoot.classList.toggle('price--on-sale', hasCompare);
    }

    const normalizeLabel = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

    function getVariantColor(variant) {
      const options = variantOptions(variant, optionCount);
      const colorIndex = optionNames.findIndex((name) => /(color|colour|颜色|顏色)/i.test(String(name || '')));
      if (colorIndex >= 0 && options[colorIndex]) return String(options[colorIndex]);
      return String(options.find((value) => /white|black|pink|purple|gray|grey|blue|red|beige|green|brown|navy|orange/i.test(String(value || ''))) || '');
    }

    function findColorThumb(variant, gallery) {
      const color = normalizeLabel(getVariantColor(variant));
      if (!color || !gallery) return null;
      const thumbs = Array.from(gallery.querySelectorAll('[data-toefix-thumb]'));
      return thumbs.find((thumb) => {
        const alt = normalizeLabel(thumb.getAttribute('data-alt') || thumb.querySelector('img')?.alt || '');
        return alt.includes(color);
      }) || null;
    }

    function updateVariantUrl(variant) {
      if (!variant?.id || !window.history?.replaceState) return;
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.get('variant') === String(variant.id)) return;
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
      } catch (_error) {
        // URL synchronization is an enhancement; never block variant selection.
      }
    }

    function updateVariantMedia(variant, cartVariant = variant) {
      if (!variant) return;
      const image = variant.featured_image || variant.featured_media?.preview_image || null;
      const imageId = image?.id || variant.featured_media?.id;
      const gallery = productInfo.querySelector('[data-toefix-desktop-gallery]');
      const colorThumb = findColorThumb(variant, gallery);
      const idThumb = imageId && gallery
        ? gallery.querySelector(`[data-media-id="${imageId}"]`)
        : null;
      // Prefer the selected color's main product photo. Shopify variants are not
      // always assigned a featured image, and some products share the first image.
      const thumb = colorThumb || idThumb || null;
      if (thumb && !thumb.classList.contains('is-active')) thumb.click();

      const mobileTrack = productInfo.querySelector('.toefix-mobile-native-gallery__track');
      const mediaId = thumb?.dataset.mediaId || imageId;
      let mobileSlide = mediaId
        ? productInfo.querySelector(`[id="toefix-mobile-media-${mediaId}"]`)
        : null;
      if (!mobileSlide) {
        const color = normalizeLabel(getVariantColor(variant));
        if (color) {
          mobileSlide = Array.from(productInfo.querySelectorAll('.toefix-mobile-native-gallery__slide')).find((slide) => {
            const alt = normalizeLabel(slide.querySelector('img')?.alt || '');
            return alt.includes(color);
          }) || null;
        }
      }
      if (mobileTrack && mobileSlide && window.matchMedia('(max-width: 749px)').matches) {
        mobileTrack.scrollTo({ left: mobileSlide.offsetLeft, behavior: 'smooth' });
      }

      // Keep the fly-to-cart image tied to item #1, while the gallery can preview
      // whichever bundle row the customer most recently changed.
      const cartImage = cartVariant?.featured_image || cartVariant?.featured_media?.preview_image || null;
      const cartImageId = cartImage?.id || cartVariant?.featured_media?.id;
      const cartThumb = findColorThumb(cartVariant, gallery) || (cartImageId && gallery
        ? gallery.querySelector(`[data-media-id="${cartImageId}"]`)
        : null);
      const imageUrl = cartThumb?.getAttribute('data-full') || cartImage?.src || cartImage?.url;
      if (imageUrl) {
        try {
          const mediaUrl = new URL(imageUrl, window.location.origin);
          mediaUrl.searchParams.set('width', '100');
          formComponent.querySelectorAll('add-to-cart-component').forEach((container) => {
            container.setAttribute('data-product-variant-media', mediaUrl.toString());
          });
        } catch (_error) {
          // Keep the existing fly-to-cart image if the variant URL cannot be parsed.
        }
      }
    }

    function updateSticky(variant) {
      if (!variant) return;
      const sticky = document.querySelector(`sticky-add-to-cart[data-product-id="${productId}"]`);
      if (!sticky) return;
      sticky.dataset.currentVariantId = String(variant.id);
      sticky.dataset.variantAvailable = String(variant.available !== false);

      const variantTitle = sticky.querySelector('.sticky-add-to-cart__variant');
      if (variantTitle) variantTitle.textContent = variant.title || '';

      const priceNode = sticky.querySelector('.sticky-add-to-cart__price .price-item--sale.price, .sticky-add-to-cart__price .price__regular .price, .sticky-add-to-cart__price .price-item--regular.price, .sticky-add-to-cart__price [data-product-price]');
      if (priceNode) priceNode.textContent = formatMoney(variant.price);

      const stickyButton = sticky.querySelector('[ref="addToCartButton"]');
      if (stickyButton) stickyButton.disabled = variant.available === false;
    }

    function syncFirstItemRows(sourceRow) {
      if (sourceRow?.dataset.itemIndex !== '1') return;
      const sourceValues = rowValues(sourceRow);
      root.querySelectorAll('[data-tfk-choice-row][data-item-index="1"]').forEach((row) => {
        if (row === sourceRow) return;
        row.querySelectorAll('[data-tfk-option-index]').forEach((select) => {
          const index = Number(select.dataset.tfkOptionIndex);
          if (sourceValues[index] !== undefined) select.value = sourceValues[index];
        });
      });
    }

    function setActiveCard(count) {
      root.querySelectorAll('[data-tfk-pack-card]').forEach((card) => {
        const active = Number(card.dataset.pack) === count;
        card.classList.toggle('is-active', active);
        const radio = card.querySelector('input[type="radio"]');
        if (radio) radio.checked = active;
        const options = card.querySelector('[data-tfk-pack-options]');
        if (options) options.hidden = !active;
      });
    }

    // The main gallery previews the bundle row the customer most recently edited.
    // The Shopify product form and URL continue to use item #1 as the primary SKU.
    let lastSelectedItemIndex = 1;

    function updateState() {
      const count = activePackCount();
      const activeCard = root.querySelector(`[data-tfk-pack-card][data-pack="${count}"]`);
      const firstVariant = findVariant(rowFor(activeCard, 1));
      const secondVariant = count === 2 ? findVariant(rowFor(activeCard, 2)) : null;
      const validFirst = Boolean(firstVariant && firstVariant.available !== false);
      const validSecond = count === 1 || Boolean(secondVariant && secondVariant.available !== false);
      const valid = validFirst && validSecond;

      if (firstVariant) {
        // Keep the actual Shopify variant ID in sync even when the selected
        // combination is sold out, so the gallery and URL still reflect color.
        variantInput.value = String(firstVariant.id);
        updateMainPrice(firstVariant);
        updateVariantUrl(firstVariant);
        updateSticky(firstVariant);
      } else {
        variantInput.value = '';
      }

      const previewItemIndex = count === 1 ? 1 : lastSelectedItemIndex;
      const previewVariant = findVariant(rowFor(activeCard, previewItemIndex)) || firstVariant;
      if (previewVariant) updateVariantMedia(previewVariant, firstVariant || previewVariant);

      secondItemInput.dataset.variantId = secondVariant ? String(secondVariant.id) : '';
      secondItemInput.checked = count === 2 && valid;

      root.dataset.valid = valid ? 'true' : 'false';
      const error = root.querySelector('[data-tfk-error]');
      if (error) {
        error.hidden = valid;
        error.textContent = count === 2 && firstVariant && !secondVariant
          ? 'Please choose a valid option combination for the second brace.'
          : 'This option combination is unavailable. Please choose another option.';
      }

      formComponent.querySelectorAll('add-to-cart-component [ref="addToCartButton"]').forEach((button) => {
        button.disabled = !valid;
      });

      const stickyButton = document.querySelector(`sticky-add-to-cart[data-product-id="${productId}"] [ref="addToCartButton"]`);
      if (stickyButton) stickyButton.disabled = !valid;

      root.querySelectorAll('[data-tfk-pack-card]').forEach(updateCardPrice);
    }

    root.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLSelectElement) && !(target instanceof HTMLInputElement)) return;

      if (target.matches('[data-tfk-option-index]')) {
        const row = target.closest('[data-tfk-choice-row]');
        lastSelectedItemIndex = Number(row?.dataset.itemIndex || 1);
        syncFirstItemRows(row);
        updateState();
        return;
      }

      if (target.matches('input[type="radio"][name="tfk-knee-pack"]')) {
        setActiveCard(Number(target.value));
        lastSelectedItemIndex = 1;
        updateState();
      }
    });

    root.querySelectorAll('[data-tfk-pack-top]').forEach((top) => {
      top.addEventListener('click', (event) => {
        if (event.target.closest('select')) return;
        const card = top.closest('[data-tfk-pack-card]');
        if (!card) return;
        setActiveCard(Number(card.dataset.pack));
        lastSelectedItemIndex = 1;
        updateState();
      });
    });

    form.addEventListener('submit', (event) => {
      if (root.dataset.valid === 'true') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const error = root.querySelector('[data-tfk-error]');
      if (error) error.hidden = false;
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, true);

    setActiveCard(2);
    syncFirstItemRows(rowFor(root.querySelector('[data-tfk-pack-card][data-pack="2"]'), 1));
    updateState();
  }

  function initAll(scope = document) {
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
    if (scope.matches?.(ROOT_SELECTOR)) initRoot(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
